import 'client-only';

import { logger } from '@curology/logger/client';
import { useContext } from 'react';

import type { DataKey } from '@/consts/dataKeys';

import { DataProviderContext } from './DataProviderContext';
import type { FetchedData, ProvidedDataShape } from './types';

type GuaranteedDataShape<Keys extends DataKey[]> = {
  [Key in Keys[number]]: FetchedData<Key>;
};

type UseDataProviderOutput<Keys extends DataKey[]> = Keys['length'] extends 0
  ? ProvidedDataShape
  : Keys['length'] extends 1
    ? FetchedData<Keys[0]>
    : GuaranteedDataShape<Keys>;

/**
 * This hook's output varies based on the number of keys provided.
 *
 * - Without keys:
 *   Returns the entire data object. All keys will be optional and no data is guaranteed to exist.
 *   Will never throw an error.
 *
 * - With one key:
 *   Returns the data corresponding to the single specified key. This guarantees that the key will exist in the returned object.
 *   Throws if the specified key is missing.
 *
 * - With multiple keys:
 *   Returns an object containing only the specified keys, with a guarantee that all provided keys will exist.
 *   Throws if any of the specified key are missing.
 */
export const useNullableDataProvider = <Keys extends DataKey[]>(
  ...keys: Keys
): UseDataProviderOutput<Keys> | null => {
  const data = useContext(DataProviderContext);

  if (!data) {
    logger.error('useDataProvider used outside of DataProvider');

    return null;
  }

  if (keys.length > 0) {
    const missingKeys = keys.filter((key) => !(key in data));

    if (missingKeys.length > 0) {
      throw new Error(
        `DataProvider did not provide keys: ${missingKeys.join(', ')}.`,
      );
    }
  }

  if (keys.length === 1) {
    return data[keys[0]];
  }

  return data as UseDataProviderOutput<Keys>;
};
